const chapter = {
  id: 'microbes-human-welfare',
  slug: 'microbes-human-welfare',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Microbes in Human Welfare',
        hindi: 'मानव कल्याण में सूक्ष्मजीव',
  },
  estReadMinutes: 45,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Biology in Human Welfare', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'household',
      title: {
        english: 'Microbes in Household Products',
        hindi: 'घरेलू उत्पादों में सूक्ष्मजीव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Curd** — *Lactobacillus acidophilus* (LAB) ferments milk → coagulation, flavour, lactic acid; ↑ vitamin B₁₂.',
        hindi: '**दही** - *लैक्टोबैसिलस एसिडोफिलस* (एलएबी) दूध को किण्वित करता है → जमावट, स्वाद, लैक्टिक एसिड; ↑ विटामिन बी₁₂.',
            },
            {
              english: '**Dough (idli, dosa, bhaturas, bread)** — fermentation by *Saccharomyces cerevisiae* (baker\'s yeast) → CO₂ → puffed dough.',
        hindi: '**आटा (इडली, डोसा, भटूरा, ब्रेड)** - *सैक्रोमाइसेस सेरेविसिया* (बेकर का खमीर) → CO₂ → फूला हुआ आटा द्वारा किण्वन।',
            },
            {
              english: '**Toddy** — sap from palms fermented by yeast → traditional drink in southern India.',
        hindi: '**ताड़ी** - खमीर द्वारा किण्वित किया गया ताड़ का रस → दक्षिणी भारत में पारंपरिक पेय।',
            },
            {
              english: '**Cheese** — selective microbial action: ',
        hindi: '**पनीर** — चयनात्मक सूक्ष्मजीवी क्रिया:',
            },
            {
              english: '• "Big holes" in **Swiss cheese** = CO₂ from *Propionibacterium sharmanii*. ',
        hindi: '• **स्विस चीज़** में "बड़े छेद" = *प्रोपियोनिबैक्टीरियम शरमानी* से CO₂।',
            },
            {
              english: '• Distinct flavour of **Roquefort cheese** ripened by **fungi (Penicillium roqueforti)**.',
        hindi: '• **रोकफोर्ट चीज़** का विशिष्ट स्वाद **कवक (पेनिसिलियम रोक्फोर्टी)** द्वारा पकाया गया।',
            },
          ],
        },
      ],
    },
    {
      id: 'industrial',
      title: {
        english: 'Microbes in Industrial Products',
        hindi: 'औद्योगिक उत्पादों में सूक्ष्मजीव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Fermented beverages** — yeast *Saccharomyces cerevisiae* (= "**brewer\'s yeast**") ferments fruit juices/cereal malts.',
        hindi: '**किण्वित पेय पदार्थ** - खमीर *सैक्रोमाइसेस सेरेविसिया* (= "**शराब बनानेवाला का खमीर**") फल juices/cereal माल्ट को किण्वित करता है।',
            },
            {
              english: '• **Without distillation**: wine, beer, cider, toddy.',
        hindi: '• **आसवन के बिना**: वाइन, बीयर, साइडर, ताड़ी।',
            },
            {
              english: '• **With distillation**: whisky, brandy, rum.',
        hindi: '• **आसवन के साथ**: व्हिस्की, ब्रांडी, रम।',
            },
            {
              english: '**Antibiotics**:',
        hindi: '**एंटीबायोटिक्स**:',
            },
            {
              english: '• **Penicillin** — from **Penicillium notatum**; discovered by **Alexander Fleming (1928)** when *Staphylococcus* culture was contaminated by mould; full potential by **Ernest Chain & Howard Florey** → used during WW II to treat injured American soldiers; trio Nobel 1945.',
        hindi: '• **पेनिसिलिन** — **पेनिसिलियम नोटेटम** से; **अलेक्जेंडर फ्लेमिंग (1928)** द्वारा खोजा गया जब *स्टैफिलोकोकस* कल्चर फफूंद द्वारा दूषित हो गया था; **अर्नेस्ट चेन और हॉवर्ड फ्लोरे** द्वारा पूर्ण क्षमता → द्वितीय विश्व युद्ध के दौरान घायल अमेरिकी सैनिकों के इलाज के लिए उपयोग किया गया; तिकड़ी नोबेल 1945.',
            },
            {
              english: '• Other antibiotics: streptomycin, tetracycline, erythromycin.',
        hindi: '• अन्य एंटीबायोटिक्स: स्ट्रेप्टोमाइसिन, टेट्रासाइक्लिन, एरिथ्रोमाइसिन।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Product', 'Microbe', 'Use'],
          rows: [
            ['Citric acid', 'Aspergillus niger', 'Acidulant in food'],
            ['Acetic acid', 'Acetobacter aceti', 'Vinegar'],
            ['Butyric acid', 'Clostridium butylicum', 'Industry'],
            ['Lactic acid', 'Lactobacillus', 'Food / medicine'],
            ['Ethanol', 'Saccharomyces cerevisiae', 'Beverages / fuel'],
            ['Streptokinase', 'Streptococcus', 'Clot buster (heart attack)'],
            ['Cyclosporin A', 'Trichoderma polysporum', 'Immunosuppressant (organ transplant)'],
            ['Statins (lovastatin)', 'Monascus purpureus', 'Lower blood cholesterol; competitively inhibit HMG-CoA reductase'],
          ],
        },
      ],
    },
    {
      id: 'sewage',
      title: {
        english: 'Microbes in Sewage Treatment',
        hindi: 'वाहित मल शोधन में सूक्ष्मजीव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Municipal waste-water treated in **STPs (Sewage Treatment Plants)** before release into rivers.',
        hindi: 'नगर निगम के अपशिष्ट जल को नदियों में छोड़ने से पहले **एसटीपी (सीवेज उपचार संयंत्र)** में उपचारित किया जाता है।',
            },
            {
              english: '**Primary treatment** — physical removal of large + small particles by **filtration + sedimentation**. Floating debris removed by sequential filtration; grit (soil + small pebbles) by sedimentation. **Primary sludge** = settled solids; effluent → secondary.',
        hindi: '**प्राथमिक उपचार** - **निस्पंदन + अवसादन** द्वारा बड़े + छोटे कणों का भौतिक निष्कासन। अनुक्रमिक निस्पंदन द्वारा हटाया गया तैरता हुआ मलबा; अवसादन द्वारा कण (मिट्टी + छोटे कंकड़)। **प्राथमिक कीचड़** = बसे हुए ठोस पदार्थ; प्रवाह → द्वितीयक.',
            },
            {
              english: '**Secondary (Biological) treatment** —',
        hindi: '**माध्यमिक (जैविक) उपचार** —',
            },
            {
              english: '• Effluent passed into large **aeration tanks**; vigorously stirred + air pumped → vigorous growth of useful aerobic microbes → form **flocs** (mesh-like with associated fungal filaments).',
        hindi: '• अपशिष्ट पदार्थ को बड़े **वातन टैंक** में प्रवाहित किया गया; जोर से हिलाया गया + हवा पंप किया गया → उपयोगी एरोबिक रोगाणुओं की जोरदार वृद्धि → **फ्लोक्स** (संबंधित फंगल फिलामेंट्स के साथ Common Trap जैसा) बनाया गया।',
            },
            {
              english: '• Microbes consume major part of organic matter → **BOD (Biochemical Oxygen Demand) drops drastically**.',
        hindi: '• सूक्ष्मजीव कार्बनिक पदार्थ का बड़ा हिस्सा खा जाते हैं → **बीओडी (बायोकेमिकल ऑक्सीजन डिमांड) में भारी गिरावट**।',
            },
            {
              english: '• BOD = O₂ consumed by 1 L of water if all organic matter were oxidised by bacteria; greater the BOD → more polluted the water.',
        hindi: '• बीओडी = ओ₂ 1 लीटर पानी द्वारा खपत यदि सभी कार्बनिक पदार्थ बैक्टीरिया द्वारा ऑक्सीकृत हो गए थे; अधिक बीओडी → पानी अधिक प्रदूषित।',
            },
            {
              english: '• Sediment removed → **activated sludge**; small portion pumped back as inoculum.',
        hindi: '• तलछट हटाया गया → **सक्रिय कीचड़**; छोटे हिस्से को इनोकुलम के रूप में वापस पंप किया जाता है।',
            },
            {
              english: '• Major part of sludge → **anaerobic sludge digesters** → bacteria digest both bacteria + fungi → produce **mixture of gases (methane, H₂S, CO₂) = biogas**.',
        hindi: '• कीचड़ का मुख्य भाग → **अवायवीय कीचड़ पचाने वाले** → बैक्टीरिया बैक्टीरिया + कवक दोनों को पचाते हैं → उत्पादन **गैसों का मिश्रण (मीथेन, H₂S, CO₂) = बायोगैस**।',
            },
            {
              english: 'Effluent is then released to natural water bodies (rivers/streams).',
        hindi: 'इसके बाद अपशिष्ट को प्राकृतिक जल निकायों (rivers/streams) में छोड़ दिया जाता है।',
            },
            {
              english: '**Ministry of Environment & Forests** initiated *Ganga Action Plan* and *Yamuna Action Plan* to save major rivers from pollution.',
        hindi: '**पर्यावरण एवं वन मंत्रालय** ने प्रमुख नदियों को प्रदूषण से बचाने के लिए *गंगा एक्शन प्लान* और *यमुना एक्शन प्लान* शुरू किया।',
            },
          ],
        },
      ],
    },
    {
      id: 'biogas',
      title: {
        english: 'Microbes in Biogas Production',
        hindi: 'बायोगैस उत्पादन में सूक्ष्मजीव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Biogas** — mixture of gases (predominantly **methane**) produced by microbial activity (~50-70 % CH₄, CO₂, H₂, H₂S).',
        hindi: '**बायोगैस** - माइक्रोबियल गतिविधि (~50-70 % CH₄, CO₂, H₂, H₂S) द्वारा उत्पादित गैसों का मिश्रण (मुख्य रूप से **मीथेन**)।',
            },
            {
              english: 'Microbes — **methanogens** (e.g. *Methanobacterium*) found in:',
        hindi: 'सूक्ष्मजीव - **मिथेनोजेन्स** (उदाहरण के लिए *मिथेनोबैक्टीरियम*) इसमें पाए जाते हैं:',
            },
            {
              english: '• Anaerobic sludge during sewage treatment.',
        hindi: '• सीवेज उपचार के दौरान अवायवीय कीचड़।',
            },
            {
              english: '• **Rumen of cattle** (helps in cellulose digestion; that\'s why cattle excreta = "**gobar**" → rich in such microbes).',
        hindi: '• **मवेशियों का रुमेन** (सेलूलोज़ पाचन में मदद करता है; इसीलिए मवेशी excreta = "**gobar**" → rich in such microbes)।',
            },
            {
              english: 'Indian **Gobar Gas Plant** — designed by **IARI (Indian Agricultural Research Institute)** + **KVIC (Khadi & Village Industries Commission)**.',
        hindi: 'भारतीय **गोबर गैस प्लांट** - **IARI (भारतीय कृषि अनुसंधान संस्थान)** + **KVIC (खादी और ग्रामोद्योग आयोग)** द्वारा डिज़ाइन किया गया।',
            },
            {
              english: 'Structure: concrete tank (10-15 ft deep) → slurry of dung → floating cover rises with gas pressure → gas piped out for cooking / lighting.',
        hindi: 'संरचना: कंक्रीट टैंक (10-15 फीट गहरा) → गोबर का घोल → गैस के दबाव के साथ फ्लोटिंग कवर ऊपर उठता है → खाना पकाने/प्रकाश के लिए पाइप से गैस निकाली जाती है।',
            },
            {
              english: 'Spent slurry = excellent fertiliser.',
        hindi: 'खर्च किया हुआ घोल = उत्कृष्ट उर्वरक।',
            },
          ],
        },
      ],
    },
    {
      id: 'biocontrol',
      title: {
        english: 'Microbes as Biocontrol Agents',
        hindi: 'जैव नियंत्रण कारकों के रूप में सूक्ष्मजीव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Reduces dependence on toxic chemical pesticides (which kill non-target beneficial organisms; pollute air, soil, water; persist in food chain).',
        hindi: 'जहरीले रासायनिक कीटनाशकों पर निर्भरता कम करता है (जो गैर-लक्ष्य लाभकारी जीवों को मारते हैं; हवा, मिट्टी, पानी को प्रदूषित करते हैं; खाद्य श्रृंखला में बने रहते हैं)।',
            },
            {
              english: '**Organic farming** — friendly approach using biocontrol + biofertilisers.',
        hindi: '**जैविक खेती** - जैव नियंत्रण + जैव उर्वरकों का उपयोग करके अनुकूल दृष्टिकोण। **लेडीबर्ड बीटल** (एफिड्स के खिलाफ) और **ड्रैगनफ्लाई** (मच्छरों के खिलाफ) - सामान्य पशु शिकारी।',
            },
            {
              english: '**Ladybird beetle** (against aphids) and **Dragonfly** (against mosquitoes) — common animal predators.',
        hindi: '**लेडीबर्ड बीटल** (एफिड्स के खिलाफ) और **ड्रैगनफ्लाई** (मच्छरों के खिलाफ) - आम पशु शिकारी।',
            },
            {
              english: '***Bacillus thuringiensis* (Bt)** — sprayed on plants; eaten by butterfly caterpillars → larvae killed (toxin activated in alkaline gut).',
        hindi: '***बैसिलस थुरिंजिएन्सिस* (बीटी)** - पौधों पर छिड़काव; तितली कैटरपिलर द्वारा खाया गया → लार्वा मारा गया (क्षारीय आंत में सक्रिय विष)।',
            },
            {
              english: '***Trichoderma*** (free-living fungus, very common in root ecosystems) — effective biocontrol against several plant pathogens.',
        hindi: '***ट्राइकोडर्मा*** (मुक्त-जीवित कवक, जड़ पारिस्थितिकी तंत्र में बहुत आम) - कई पौधों के रोगजनकों के खिलाफ प्रभावी जैव नियंत्रण।',
            },
            {
              english: '**Baculoviruses** (esp. genus *Nucleopolyhedrovirus*) — narrow species-specific insecticidal application; ideal for **IPM (Integrated Pest Management)**; safe to plants, mammals, birds, fish, non-target insects.',
        hindi: '**बेकुलोवायरस** (विशेष रूप से जीनस *न्यूक्लियोपॉलीहेड्रोवायरस*) - संकीर्ण प्रजाति-विशिष्ट कीटनाशक अनुप्रयोग; **आईपीएम (एकीकृत कीट प्रबंधन)** के लिए आदर्श; पौधों, स्तनधारियों, पक्षियों, मछलियों, गैर-लक्षित कीड़ों के लिए सुरक्षित।',
            },
          ],
        },
      ],
    },
    {
      id: 'biofertilisers',
      title: {
        english: 'Microbes as Biofertilisers',
        hindi: 'जैव उर्वरकों के रूप में सूक्ष्मजीव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Biofertilisers** — organisms that enrich nutrient quality of soil. Three main groups:',
        hindi: '**जैवउर्वरक**-जीव जो मिट्टी की पोषक गुणवत्ता को समृद्ध करते हैं। तीन मुख्य समूह:',
            },
            {
              english: '**1. Bacteria**:',
        hindi: '**1. बैक्टीरिया**:',
            },
            {
              english: '• Symbiotic — ***Rhizobium*** in root nodules of legumes (fix atmospheric N₂).',
        hindi: '• सहजीवी - फलियों की जड़ की गांठों में ***राइज़ोबियम*** (वायुमंडलीय N₂ को स्थिर करें)।',
            },
            {
              english: '• Free-living — ***Azotobacter, Azospirillum*** (free-living N₂-fixers in soil).',
        hindi: '• मुक्त-जीवित - ***एज़ोटोबैक्टर, एज़ोस्पिरिलम*** (मिट्टी में मुक्त-जीवित एन₂-फिक्सर)।',
            },
            {
              english: '**2. Cyanobacteria** (BGA) — autotrophic; widely distributed in aquatic + terrestrial; ***Anabaena, Nostoc, Oscillatoria*** add organic matter + fix N₂. In paddy fields, BGA serve as important biofertilisers. ***Anabaena azollae*** lives in leaf cavities of water fern *Azolla*.',
        hindi: '**2. सायनोबैक्टीरिया** (बीजीए) - स्वपोषी; जलीय + स्थलीय में व्यापक रूप से वितरित; ***अनाबेना, नोस्टॉक, ऑसिलेटोरिया*** कार्बनिक पदार्थ जोड़ें + N₂ ठीक करें। धान के खेतों में, बीजीए महत्वपूर्ण जैव उर्वरक के रूप में काम करता है। ***अनाबेना एजोलाए*** पानी के फ़र्न *एज़ोला* की पत्ती की गुहाओं में रहता है।',
            },
            {
              english: '**3. Fungi** — ***Mycorrhiza*** = symbiosis between fungi (mainly genus ***Glomus***) + roots of higher plants. Fungus absorbs **phosphorus** from soil + passes to plant. Also: ↑ tolerance to drought/salt, ↑ growth, ↑ resistance to root pathogens.',
        hindi: '**3. कवक** - ***माइकोराइजा*** = कवक (मुख्य रूप से जीनस ***ग्लोमस***) + उच्च पौधों की जड़ों के बीच सहजीवन। कवक मिट्टी से **फॉस्फोरस** को अवशोषित करता है + पौधे तक पहुंचता है। इसके अलावा: ↑ drought/salt के प्रति सहनशीलता, ↑ वृद्धि, ↑ जड़ रोगजनकों के प्रति प्रतिरोध।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Curd} = \\text{Lactobacillus acidophilus (LAB)}',
      desc: {
        english: '',
        hindi: 'दही = लैक्टोबैसिलस एसिडोफिलस (LAB)',
      },
    },
    {
      latex: '\\text{Bread/dosa: Saccharomyces cerevisiae (CO}_2\\text{)}',
      desc: {
        english: '',
        hindi: 'ब्रेड/डोसा: सैक्रोमाइसेस सेरेविसिया (सीओ 2)',
      },
    },
    {
      latex: '\\text{Penicillin: Penicillium notatum (Fleming, 1928)}',
      desc: {
        english: '',
        hindi: 'पेनिसिलिन: पेनिसिलियम नोटेटम (फ्लेमिंग, 1928)',
      },
    },
    {
      latex: '\\text{Cyclosporin A (Trichoderma) = immunosuppressant}',
      desc: {
        english: '',
        hindi: 'साइक्लोस्पोरिन ए (ट्राइकोडर्मा) = प्रतिरक्षादमनकारी',
      },
    },
    {
      latex: '\\text{Lovastatin (Monascus purpureus) inhibits HMG-CoA reductase}',
      desc: {
        english: '',
        hindi: 'लोवास्टैटिन (मोनस्कस परप्यूरियस) एचएमजी-सीओए रिडक्टेस को रोकता है',
      },
    },
    {
      latex: '\\text{Biogas} \\approx 50\\text{-}70\\% \\text{ CH}_4 \\text{ (Methanobacterium)}',
      desc: {
        english: '',
        hindi: 'बायोगैस 50-70\\% सीएच 4 (मेथनोबैक्टीरियम)',
      },
    },
    {
      latex: '\\text{Mycorrhiza (Glomus) absorbs P; Anabaena azollae fixes N}_2',
      desc: {
        english: '',
        hindi: 'माइकोराइजा (ग्लोमस) पी को अवशोषित करता है; एनाबेना एजोलाए एन 2 को ठीक करता है',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Drug',
      problem: {
        english: 'Cyclosporin A is obtained from:',
        hindi: 'साइक्लोस्पोरिन ए प्राप्त होता है:',
      },
      answer: {
        english: 'Trichoderma polysporum',
        hindi: 'ट्राइकोडर्मा पॉलीस्पोरम',
      },
      solution: {
        english: 'Used as immunosuppressant in organ transplants.',
        hindi: 'अंग प्रत्यारोपण में प्रतिरक्षादमनकारी के रूप में उपयोग किया जाता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Sewage',
      problem: {
        english: 'Biogas mainly contains:',
        hindi: 'बायोगैस में मुख्य रूप से शामिल हैं:',
      },
      answer: {
        english: 'Methane',
        hindi: 'मीथेन',
      },
      solution: {
        english: '50-70 % CH₄ produced by methanogens.',
        hindi: '50-70 % CH₄ मीथेनोजेन्स द्वारा उत्पादित।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Statin',
      problem: {
        english: 'Lovastatin lowers blood cholesterol by:',
        hindi: 'लवस्टैटिन रक्त कोलेस्ट्रॉल को कम करता है:',
      },
      answer: {
        english: 'Inhibiting HMG-CoA reductase',
        hindi: 'HMG-CoA रिडक्टेस को रोकना',
      },
      solution: {
        english: 'Competitive inhibition; produced by Monascus purpureus.',
        hindi: 'प्रतिस्पर्धी निषेध; मोनास्कस पुरप्यूरियस द्वारा निर्मित।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Biofertiliser',
      problem: {
        english: 'Symbiotic association between fungus and roots is called:',
        hindi: 'कवक और जड़ों के बीच सहजीवी संबंध को कहा जाता है:',
      },
      answer: {
        english: 'Mycorrhiza',
        hindi: 'माइकोराइजा',
      },
      solution: {
        english: 'Glomus species; absorbs phosphorus.',
        hindi: 'ग्लोमस प्रजाति; फॉस्फोरस को अवशोषित करता है।',
      },
    },
    {
      year: 2018,
      exam: 'NEET',
      tag: 'Curd',
      problem: {
        english: 'LAB used in curd formation is:',
        hindi: 'दही बनाने में प्रयुक्त LAB है:',
      },
      answer: {
        english: 'Lactobacillus acidophilus',
        hindi: 'लैक्टोबैसिलस एसिडोफिलस',
      },
      solution: {
        english: 'Increases vitamin B₁₂; ferments milk → coagulation.',
        hindi: 'विटामिन बी₁₂ बढ़ाता है; दूध को किण्वित करता है → स्कंदन।',
      },
    },
  ],
  next: {
    slug: 'human-health-disease',
    title: 'Human Health and Disease',
  },
};

export default chapter;
